import React from 'react';

function fmtShortDate(d) {
  if (!d) return '';
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

export default function WeightChart({ entries, goalWeight }) {
  const data = [...(entries || [])].sort((a, b) => a.date.localeCompare(b.date)).slice(-20);

  if (data.length < 2) {
    return (
      <div className="flex flex-col items-center justify-center h-36 text-[#A8A29E] text-sm font-body gap-2">
        <div className="w-10 h-10 bg-[#FFEDD5] rounded-full flex items-center justify-center">
          <svg className="w-5 h-5 text-[#F59E0B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        </div>
        <span>Add at least 2 entries to see the chart</span>
      </div>
    );
  }

  const W = 600, H = 180;
  const PAD = { top: 20, right: 20, bottom: 38, left: 46 };
  const cW = W - PAD.left - PAD.right;
  const cH = H - PAD.top - PAD.bottom;

  const weights = data.map(e => parseFloat(e.weight));
  const all = goalWeight ? [...weights, parseFloat(goalWeight)] : weights;
  const minW = Math.floor(Math.min(...all)) - 1;
  const maxW = Math.ceil(Math.max(...all)) + 1;
  const range = maxW - minW || 1;

  const xS = (i) => PAD.left + (data.length > 1 ? (i / (data.length - 1)) * cW : cW / 2);
  const yS = (w) => PAD.top + cH - ((w - minW) / range) * cH;

  const linePoints = data.map((e, i) => `${xS(i)},${yS(e.weight)}`).join(' ');
  const areaPoints = `${xS(0)},${PAD.top + cH} ${linePoints} ${xS(data.length - 1)},${PAD.top + cH}`;

  // Y-axis ticks
  const yTicks = [minW, Math.round((minW + maxW) / 2), maxW];

  // X-axis labels: first, middle, last
  const xIdxs = [0, Math.floor((data.length - 1) / 2), data.length - 1].filter((v, i, a) => a.indexOf(v) === i);

  const goalY = goalWeight ? yS(parseFloat(goalWeight)) : null;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id="wg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#F59E0B" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#F59E0B" stopOpacity="0.01" />
        </linearGradient>
      </defs>

      {/* Horizontal grid lines */}
      {yTicks.map(w => (
        <line key={w} x1={PAD.left} x2={W - PAD.right} y1={yS(w)} y2={yS(w)}
          stroke="#E7E5E4" strokeWidth="1" />
      ))}

      {/* Goal line */}
      {goalY !== null && (
        <>
          <line x1={PAD.left} x2={W - PAD.right} y1={goalY} y2={goalY}
            stroke="#10B981" strokeWidth="1.5" strokeDasharray="6,4" />
          <text x={W - PAD.right + 4} y={goalY + 4} fontSize="10" fill="#10B981" fontFamily="DM Sans">Goal</text>
        </>
      )}

      {/* Area fill */}
      <polygon points={areaPoints} fill="url(#wg)" />

      {/* Main line */}
      <polyline points={linePoints} fill="none" stroke="#F59E0B" strokeWidth="2.5"
        strokeLinecap="round" strokeLinejoin="round" />

      {/* Data dots */}
      {data.map((e, i) => (
        <g key={i}>
          <circle cx={xS(i)} cy={yS(e.weight)} r="5" fill="white" stroke="#F59E0B" strokeWidth="2.5" />
        </g>
      ))}

      {/* Y-axis labels */}
      {yTicks.map(w => (
        <text key={w} x={PAD.left - 6} y={yS(w) + 4} textAnchor="end"
          fontSize="11" fill="#A8A29E" fontFamily="DM Sans">{w}</text>
      ))}

      {/* X-axis labels */}
      {xIdxs.map(i => (
        <text key={i} x={xS(i)} y={H - 6} textAnchor="middle"
          fontSize="11" fill="#A8A29E" fontFamily="DM Sans">
          {fmtShortDate(data[i].date)}
        </text>
      ))}
    </svg>
  );
}
